import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AppBar from '../components/AppBar'
import LoadingSpinner from '../components/LoadingSpinner'
import {
  universityApi,
  University as Univ,
  UniversityMembership,
} from '../api/university'

/**
 * 대학교 연결 — 재학 중인 학교 고르기.
 *
 * Figma `FINAL`(6360:2082) 대로 그렸다.
 *
 * 목록은 서버에서 받는다. 파일럿 학교가 정해지면 서버에서 `is_active` 를 끄는
 * 것으로 끝나야 하기 때문이다 — 웹 상수였다면 배포를 다시 해야 한다.
 *
 * 이미 연결한 사람은 고를 게 없으므로 **연결된 학교 한 줄만** 보여준다.
 * 바꾸는 흐름은 아직 없다(한 사람 = 학교 하나).
 */
const University: React.FC = () => {
  const navigate = useNavigate()

  const [universities, setUniversities] = useState<Univ[]>([])
  const [membership, setMembership] = useState<UniversityMembership | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let alive = true
    let redirected = false
    ;(async () => {
      try {
        const [list, mine] = await Promise.all([
          universityApi.getUniversities(),
          universityApi.getMembership(),
        ])
        if (!alive) return
        // 이미 인증한 사람은 고를 게 없다 — 바로 학교 그룹으로.
        // 소속을 해제한 사람은 '다시 들어가기'로 (메일 인증 없이 돌아온다)
        if (mine) {
          redirected = true
          navigate(mine.leftAt ? '/university/rejoin' : '/university/group', { replace: true })
          return
        }
        setUniversities(list)
        setMembership(mine)
      } catch {
        if (alive) setFailed(true)
      } finally {
        // 그룹으로 넘기는 중이면 목록 화면을 그리지 않는다(로딩 그대로)
        if (alive && !redirected) setIsLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [navigate])

  const goVerify = (u: Univ) => {
    navigate(
      `/university/email?id=${u.id}` +
        `&name=${encodeURIComponent(u.name)}` +
        `&domain=${encodeURIComponent(u.domain)}`
    )
  }

  const row = (u: Univ, onClick?: () => void) => {
    const inner = (
      <>
        {u.logoPath && (
          <img
            src={u.logoPath}
            alt=""
            className="shrink-0 w-40px h-40px rounded-full object-cover"
          />
        )}
        <div className="flex-1 min-w-0 flex flex-col gap-2px text-left">
          <p className="text-style-labelL text-brandPrimary tracking-[-0.16px] leading-[1.4]">
            {u.name}
          </p>
          <p className="text-style-captionS text-textColorText2 tracking-[-0.12px] leading-[1.4]">
            @{u.domain}
          </p>
        </div>
      </>
    )
    const cls =
      'w-full flex items-center gap-12px py-12px border-b border-divider-divider-1'
    return onClick ? (
      <button key={u.key} type="button" onClick={onClick} className={`${cls} active:opacity-60`}>
        {inner}
      </button>
    ) : (
      <div key={u.key} className={cls}>
        {inner}
      </div>
    )
  }

  /*
   * 불러오는 동안은 제목도 그리지 않는다. 인증한 사람은 곧 그룹 화면으로
   * 넘어가는데, 그 사이에 '재학중인 대학교를 선택해주세요'가 잠깐 비쳤다.
   */
  if (isLoading) {
    return (
      <div className="w-full min-h-full flex flex-col bg-backgroundColorBg2">
        <AppBar />
        <div className="py-80px flex justify-center">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  return (
    <div className="w-full min-h-full flex flex-col bg-backgroundColorBg2">
      <AppBar />

      <div className="app-container flex flex-col gap-16px px-20px pt-52px pb-32px">
        <div className="w-full flex flex-col gap-12px">
          <h1 className="text-style-title2 text-brandPrimary tracking-[-0.6px]">
            {membership ? '연결된 대학교예요' : '재학중인 대학교를 선택해주세요'}
          </h1>
          <p className="text-style-body2 text-textColorText2 tracking-[-0.14px] leading-[1.4]">
            {membership ? membership.email : '학교 이메일로 인증해 주세요'}
          </p>
        </div>

        {isLoading ? (
          <div className="w-full py-40px flex justify-center">
            <LoadingSpinner />
          </div>
        ) : failed ? (
          <div className="w-full py-40px text-center text-style-body1 text-textColorText3">
            잠시 후 다시 시도해주세요
          </div>
        ) : membership ? (
          <div className="w-full flex flex-col">{row(membership.university)}</div>
        ) : (
          <>
            <div className="w-full flex flex-col">
              {universities.map((u) => row(u, () => goVerify(u)))}
            </div>

            <div className="w-full flex items-center gap-8px px-14px py-12px rounded-12px bg-[#E9E1D5]">
              <img
                src="/images/university/info-icon.svg"
                alt=""
                className="shrink-0 w-18px h-18px"
              />
              <p className="flex-1 min-w-0 font-pretendard font-bold text-[13.5px] text-textColorText2 tracking-[-0.135px] leading-[1.4]">
                다른 대학교도 곧 열릴 예정이에요
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default University
